import { useEffect, useState } from "react";
import { ClassificationStatus } from "../utils";

import { v4 as uuidv4 } from "uuid";
import { message } from "antd";

export const useHorizontalBoardActions = (
	initialData,
	showArchived,
	onUpdateOrder,
	onDeleteClassifications,
	onEditClassifications,
) => {
	const [categories, setCategories] = useState([]);

	useEffect(() => {
		let visibleData = [...initialData];
		
		if (!showArchived) {			
			const filterArchivedRecursively = (items) => {
				return items.map(item => {
					const filteredChildren = item.children?.length 
						? filterArchivedRecursively(item.children.filter(child => !child.archived))
						: [];
						
					return {
						...item,
						children: filteredChildren,
						children_ids: filteredChildren.map(child => child.id)
					};
				}).filter(item => !item.archived);
			};
			
			visibleData = filterArchivedRecursively(visibleData);
		}
	
		setCategories(visibleData);
	}, [initialData, showArchived]);

	const handleDragEnd = (result) => {
		if (!result.destination) return;

		const { source, destination } = result;
		const changedItems = [];
		let movedItemId = null;

		// Obtener una referencia a los elementos visibles solamente
		const getVisibleItems = (items) => {
			return showArchived
				? items
				: items.filter((item) => !item.archived);
		};

		const mapVisibleIndexToRealIndex = (items, visibleIndex) => {
			const visibleItems = getVisibleItems(items);
			const visibleItem = visibleItems[visibleIndex];
			if (!visibleItem) return items.length; // Última posición real
			return items.findIndex((item) => item.id === visibleItem.id);
		};
		
		const moveItem = (list, fromVisibleIndex, toVisibleIndex) => {
			const result = [...list];
		
			const fromRealIndex = showArchived
				? fromVisibleIndex
				: mapVisibleIndexToRealIndex(result, fromVisibleIndex);
		
			let toRealIndex = showArchived
				? toVisibleIndex
				: mapVisibleIndexToRealIndex(result, toVisibleIndex);
		
			// Si el destino es más allá del final del array, ajusta al final
			if (toRealIndex > result.length - 1) {
				toRealIndex = result.length;
			}
		
			const [removed] = result.splice(fromRealIndex, 1);
			movedItemId = removed.id;
			result.splice(toRealIndex, 0, removed);
		
			return result.map((item, index) => {
				if (item.id === movedItemId || item.sort_order !== index) {
					changedItems.push({
						id: item.id,
						sort_order: index,
						status:
							item.status === ClassificationStatus.NORMAL
								? ClassificationStatus.EDITED
								: item.status,
					});
		
					return {
						...item,
						sort_order: index,
						status:
							item.status === ClassificationStatus.NORMAL
								? ClassificationStatus.EDITED
								: item.status,
					};
				}
				return item;
			});
		};

		const recursiveMove = (items) => {
			return items.map((item) => {
				if (item.id === source.droppableId) {
					const previousLength = changedItems.length;
					const updatedChildren = moveItem(
						item.children,
						source.index,
						destination.index
					);

					// Si alguno de los hijos cambió, también marcamos al padre como cambiado
					if (changedItems.length > previousLength) {
						changedItems.push({
							id: item.id,
							status:
								item.status === ClassificationStatus.NORMAL
									? ClassificationStatus.EDITED
									: item.status,
						});
					}

					return {
						...item,
						children: updatedChildren,
						status:
							item.status === ClassificationStatus.NORMAL
								? ClassificationStatus.EDITED
								: item.status,
					};
				} else if (item.children?.length) {
					const previousLength = changedItems.length;
					const updatedItem = {
						...item,
						children: recursiveMove(item.children),
					};

					// Si alguno de los hijos cambió, también marcamos al padre como cambiado
					if (changedItems.length > previousLength) {
						changedItems.push({
							id: item.id,
							status:
								item.status === ClassificationStatus.NORMAL
									? ClassificationStatus.EDITED
									: item.status,
						});
					}

					return updatedItem;
				}
				return item;
			});
		};

		let updated;
		if (source.droppableId === "categories") {
			updated = moveItem(categories, source.index, destination.index);
			setCategories(updated);
		} else {
			updated = recursiveMove(categories);
			setCategories(updated);
		}

		onUpdateOrder(updated, false, changedItems);
	};

	const handleAddSubCategory = (catId, newCategory) => {
		if (!newCategory) return;

		let newChild = {};
		let parentNode = {};

		const updatedCategories = categories.map((cat) => {
			if (cat.id === catId) {
				newChild = {
					id: uuidv4(),
					classification: `${cat.classification}-${newCategory}`,
					classification_name: newCategory,
					sort_order: cat.children.length,
					depth: (cat.depth || 0) + 1,
					parent_id: catId,
					children_ids: [],
					children: [],
					status: ClassificationStatus.NEW,
					archived: false,
				};

				parentNode = cat;

				return {
					...cat,
					children: [...cat.children, newChild],
					children_ids: [...(cat.children_ids || []), newChild.id],
				};
			}
			return cat;
		});

		__sendNewDataManegment(updatedCategories, newChild, parentNode);
	};

	const addClassificationToNode = (targetId) => {
		let newChild = {};
		let parentNode = {};

		const recursivelyUpdate = (nodes) => {
			return nodes.map((node) => {
				if (node.id === targetId) {
					newChild = {
						id: uuidv4(),
						classification: `${node.classification}-Nueva_Clasificacion`,
						classification_name: "Nueva_Clasificacion",
						sort_order: node.children.length,
						depth: (node.depth || 1) + 1,
						parent_id: targetId,
						children_ids: [],
						children: [],
						status: ClassificationStatus.NEW,
						archived: false,
					};

					parentNode = node;

					return {
						...node,
						children: [...node.children, newChild],
						children_ids: [
							...(node.children_ids || []),
							newChild.id,
						],
					};
				} else if (node.children?.length) {
					return {
						...node,
						children: recursivelyUpdate(node.children),
					};
				}
				return node;
			});
		};

		const updated = recursivelyUpdate(categories);
		__sendNewDataManegment(updated, newChild, parentNode);
	};
	
	const __sendNewDataManegment = (updated, newChild, parentNode) => {
		setCategories(updated);
		onUpdateOrder(updated, true);

		const {
			id: parentId,
			children_ids: parentChildrenIds,
			status,
		} = parentNode;

		const updatingChildrenIds = {
			[parentId]: {
				children_ids: [...parentChildrenIds, newChild.id],
			},
		};

		const isNewRegisterToCheck = status === ClassificationStatus.NEW;

		onEditClassifications({
			type: isNewRegisterToCheck ? "UPDATE_NEW" : "UPDATE",
			ids: [parentNode.id],
			updatedCategories: updated,
			updateData: updatingChildrenIds,
			extra: { newChild },
		});
	};

	const renameClassificationNode = (id, newName) => {
		// Recopilamos los nodos afectados para enviarlos al backend
		const affectedNodes = [];
		const newNodesIds = []; // Para guardar IDs de nodos con estatus NEW

		const recursivelyRename = (nodes, parentPath = "") => {
			return nodes.map((node) => {
				if (node.id === id) {
					const newFullClassification = parentPath
						? `${parentPath}-${newName}`
						: newName;

					// Función para actualizar las clasificaciones de los hijos y recopilarlos
					const updateChildrenClassification = (
						children,
						parentClassification
					) => {
						return children.map((child) => {
							const newChildClassification = `${parentClassification}-${child.classification_name}`;
							const updatedChild = {
								...child,
								classification: newChildClassification,
								// Si el estatus es NEW, mantenerlo como NEW
								status:
									child.status === ClassificationStatus.NEW
										? ClassificationStatus.NEW
										: child.status ===
											ClassificationStatus.NORMAL
											? ClassificationStatus.EDITED
											: child.status,
							};

							// Agregamos el hijo afectado a nuestra lista solo si no es NEW
							if (
								updatedChild.status === ClassificationStatus.NEW
							) {
								newNodesIds.push(child.id);
							} else {
								affectedNodes.push({
									id: child.id,
									classification: newChildClassification,
									status: updatedChild.status,
								});
							}

							if (child.children?.length) {
								updatedChild.children =
									updateChildrenClassification(
										child.children,
										newChildClassification
									);
							}
							return updatedChild;
						});
					};

					const updatedNode = {
						...node,
						classification_name: newName,
						classification: newFullClassification,
						// Si el estatus es NEW, mantenerlo como NEW
						status:
							node.status === ClassificationStatus.NEW
								? ClassificationStatus.NEW
								: ClassificationStatus.EDITED,
						children: updateChildrenClassification(
							node.children || [],
							newFullClassification
						),
					};

					// Agregamos el nodo principal afectado a nuestra lista solo si no es NEW
					if (updatedNode.status === ClassificationStatus.NEW) {
						newNodesIds.push(updatedNode.id);
					} else {
						affectedNodes.push({
							id: updatedNode.id,
							classification_name: newName,
							classification: newFullClassification,
							status: updatedNode.status,
						});
					}

					return updatedNode;
				} else if (node.children?.length) {
					return {
						...node,
						children: recursivelyRename(
							node.children,
							node.classification
						),
					};
				}
				return node;
			});
		};

		const updatedCategories = recursivelyRename(categories);
		setCategories(updatedCategories);
		onUpdateOrder(updatedCategories, true);

		// Para los nodos NEW, debemos actualizar la operación CREATE original
		if (newNodesIds.length > 0) {
			onEditClassifications({
				type: "UPDATE_NEW",
				ids: newNodesIds,
				updatedCategories,
			});
		}

		// Enviamos los nodos afectados que no son NEW al backend
		if (affectedNodes.length > 0) {
			const affectedIds = affectedNodes.map((node) => node.id);
			const updateData = affectedNodes.reduce((acc, node) => {
				acc[node.id] = {
					...(node.classification_name && {
						classification_name: node.classification_name,
					}),
					classification: node.classification,
					status: node.status,
				};
				return acc;
			}, {});

			onEditClassifications({
				type: "UPDATE",
				ids: affectedIds,
				updateData: updateData,
			});
		}
	};
	
	const handleDeleteNode = (targetId) => {
		const idsToDelete = [];

		// Función para recopilar todos los IDs a eliminar
		const collectIds = (node) => {
			if (node.isInUse) {
				message.info(
					`No se puede eliminar la clasificación "${node.classification_name}" en uso`
				);
				return;
			}
			idsToDelete.push(node.id);
			node.children?.forEach(collectIds);
		};

		// Eliminar directamente los nodos en lugar de marcarlos
		const recursivelyFilterAndClean = (nodes) => {
			return nodes
				.filter((node) => {
					if (node.id === targetId) {
						if (node.isInUse) {
							message.info(
								`No se puede eliminar la clasificación "${node.classification_name}" en uso`
							);
							return true; // Mantener el nodo si está en uso
						}

						collectIds(node);
						return false; // Eliminar este nodo
					}
					return true; // Mantener otros nodos
				})
				.map((node) => {
					if (node.children?.length) {
						return {
							...node,
							children: recursivelyFilterAndClean(node.children),
							children_ids: node.children_ids.filter(
								(id) => !idsToDelete.includes(id)
							),
						};
					}
					return node;
				});
		};

		const updatedCategories = recursivelyFilterAndClean(categories);
		setCategories(updatedCategories);
		onDeleteClassifications(idsToDelete);
		onUpdateOrder(updatedCategories);
	};
		
	return {
		categories,
		handleDragEnd,
		handleAddSubCategory,
		addClassificationToNode,
		renameClassificationNode,
		handleDeleteNode,
	};
};
